import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Ruler, TrendingUp, Landmark, Activity, FileDown } from "lucide-react";
import api from "@/lib/api";
import { formatEUR, statusColor } from "@/lib/format";
import { useLang } from "@/i18n";
import { LangToggle } from "@/components/Navbar";
import { Reveal } from "@/components/Reveal";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { Seo } from "@/components/Seo";
import { SIXT12_TITLE_MATCH, TRILOCALE_VENDUTO } from "@/config/trilocaleLanding";

const opDescription = (t, op) =>
  t("seo_op_desc")
    .replace("{location}", op.location)
    .replace("{type}", op.type)
    .replace("{mq}", Number(op.mq).toLocaleString("it-IT"))
    .replace("{status}", op.status);

export default function OperationPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [op, setOp] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/operations/${id}`, { params: { lang } })
      .then((r) => setOp(r.data))
      .catch(() => setNotFound(true));
  }, [id, lang]);

  if (notFound) {
    return (
      <main className="min-h-screen bg-[#0A0A0C] text-zinc-100 flex flex-col items-center justify-center gap-6 grain-overlay">
        <Seo title={`${t("op_notfound")} — Capital Stone S.r.l.`} description={t("op_notfound")} path={`/operazioni/${id}`} noindex lang={lang} />
        <p className="font-display text-3xl" data-testid="operation-not-found">{t("op_notfound")}</p>
        <Link to="/" className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] border border-amber-500/30 px-6 py-3 hover:bg-amber-500/10 transition-colors duration-300">
          ← Capital Stone
        </Link>
      </main>
    );
  }

  if (!op) {
    return (
      <main className="min-h-screen bg-[#0A0A0C] flex items-center justify-center">
        <p className="text-zinc-500 text-sm tracking-[0.2em] uppercase">Capital Stone...</p>
      </main>
    );
  }

  const metrics = [
    { icon: Landmark, label: t("op_value"), value: formatEUR(op.value), gold: true },
    { icon: TrendingUp, label: t("op_roi"), value: op.roi },
    { icon: Ruler, label: t("op_area"), value: `${Number(op.mq).toLocaleString("it-IT")} mq` },
    { icon: Activity, label: t("op_status"), value: op.status },
  ];

  return (
    <main className="grain-overlay bg-[#0A0A0C] text-zinc-100 min-h-screen" data-testid="operation-page">
      <Seo
        title={`${op.title} — Capital Stone S.r.l.`}
        description={opDescription(t, op)}
        path={`/operazioni/${op.id}`}
        image={op.image_url}
        type="article"
        lang={lang}
      />
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0C]/85 backdrop-blur-xl border-b border-amber-500/10">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between h-[72px]">
          <Link to="/" data-testid="op-nav-logo" className="flex items-center gap-3 group">
            <img
              src="/logo.png"
              alt="Capital Stone — Investimenti Immobiliari"
              className="h-9 sm:h-10 w-auto transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </Link>
          <div className="flex items-center gap-4">
            <LangToggle />
            <Link
              to="/"
              data-testid="op-back-link"
              className="hidden sm:flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-zinc-400 hover:text-[#D4AF37] transition-colors duration-300"
            >
              <ArrowLeft size={14} /> {t("op_back")}
            </Link>
          </div>
        </div>
      </header>

      <section className="relative h-[62vh] min-h-[420px] overflow-hidden">
        <motion.img
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          src={op.image_url}
          alt={op.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/35 to-[#0A0A0C]/40" />
        <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-12">
          <motion.div initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}>
            <span className={`inline-block text-[10px] tracking-[0.25em] uppercase font-mono border px-3 py-1.5 backdrop-blur mb-5 ${statusColor(op.status)}`}>
              {op.status}
            </span>
            {TRILOCALE_VENDUTO && SIXT12_TITLE_MATCH.test(op.title) && (
              <span data-testid="operation-sold-badge" className="inline-block ml-2 text-[10px] tracking-[0.25em] uppercase font-mono border border-red-500/60 bg-red-600/90 text-white px-3 py-1.5 backdrop-blur mb-5">
                {lang === "en" ? "Two-bedroom: SOLD" : "Trilocale: VENDUTO"}
              </span>
            )}
            <p className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-mono text-amber-400/90">
              <MapPin size={12} /> {op.location}
            </p>
            <h1 data-testid="operation-title" className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-zinc-100 mt-3">
              {op.title}
            </h1>
            <p className="text-sm text-zinc-400 mt-2">{op.type}</p>
          </motion.div>
        </div>
      </section>

      <section className="border-y border-amber-500/10 bg-[#0D0D10]">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4">
          {metrics.map((m) => (
            <div key={m.label} className="py-8 px-4 border-l first:border-l-0 border-white/5" data-testid={`metric-${m.label}`}>
              <p className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                <m.icon size={12} className="text-[#D4AF37]" /> {m.label}
              </p>
              <p className={`font-display text-xl sm:text-3xl mt-3 ${m.gold ? "text-gold" : "text-zinc-100"}`}>{m.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7">
          <p className="text-sm sm:text-lg text-zinc-300 leading-relaxed" data-testid="operation-description">{op.description}</p>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="border border-amber-500/15 bg-[#121216] p-8">
            <p className="font-display text-2xl text-zinc-100">{t("op_cta_t")}</p>
            <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{t("op_cta_x")}</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link
                to="/#contatti"
                data-testid="operation-inquire-btn"
                className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A0A0C] text-xs tracking-[0.2em] uppercase font-semibold px-7 py-4 hover:bg-[#F3E5AB] transition-colors duration-300"
              >
                {t("op_inquire")}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/operations/${op.id}/dossier.pdf?lang=${lang}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="operation-dossier-btn"
                className="inline-flex items-center gap-3 border border-amber-500/40 text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-semibold px-7 py-4 hover:bg-amber-500/10 transition-colors duration-300"
              >
                <FileDown size={15} /> {t("op_dossier")}
              </a>
              {op.listing_url && (
                <a
                  href={op.listing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="operation-listing-btn"
                  className="inline-flex items-center gap-3 border border-white/20 text-zinc-300 text-xs tracking-[0.2em] uppercase font-semibold px-7 py-4 hover:border-amber-500/50 hover:text-[#D4AF37] transition-colors duration-300"
                >
                  <ArrowRight size={15} /> {t("op_listing")}
                </a>
              )}
            </div>
          </div>
        </Reveal>
      </section>

      {op.before_image_url && (
        <section className="pb-16 lg:pb-24 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="operation-before-after">
          <Reveal>
            <p className="text-xs tracking-[0.35em] uppercase font-mono text-amber-400/90 mb-6">
              {t("ba_overline")} — {t("ba_h1")} / {t("ba_after")}
            </p>
            <BeforeAfterSlider before={op.before_image_url} after={op.image_url} aspect="aspect-[16/9]" />
          </Reveal>
        </section>
      )}

      {op.timeline?.length > 0 && (
        <section className="pb-16 lg:pb-24 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="operation-timeline">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-zinc-100 mb-12">{t("op_timeline")}</h2>
          </Reveal>
          <ol className="relative border-l border-amber-500/20 ml-2 space-y-12">
            {op.timeline.map((step, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <li className="pl-10 relative" data-testid={`timeline-step-${i}`}>
                  <span className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rotate-45 bg-[#0A0A0C] border border-[#D4AF37]" />
                  <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4AF37]">{step.date}</p>
                  <h3 className="font-display text-2xl font-semibold text-zinc-100 mt-2">{step.phase}</h3>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed max-w-2xl">{step.description}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </section>
      )}

      {op.gallery?.length > 0 && (
        <section className="pb-16 lg:pb-24 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="operation-gallery">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-zinc-100 mb-10">{t("op_gallery")}</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {op.gallery.map((url, i) => (
              <Reveal key={i} delay={(i % 3) * 0.1}>
                <div className="group relative aspect-[4/3] overflow-hidden border border-white/10" data-testid={`gallery-item-${i}`}>
                  <img
                    src={url}
                    alt={`${op.title} — ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover saturate-[0.7] transition-[transform,saturate] duration-700 group-hover:scale-105 group-hover:saturate-100"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {op.videos?.length > 0 && (
        <section className="pb-16 lg:pb-24 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="operation-videos">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-zinc-100 mb-10">{t("op_videos")}</h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {op.videos.map((url, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="relative border border-amber-500/15 bg-black overflow-hidden gold-frame" data-testid={`video-item-${i}`}>
                  <video src={url} controls preload="metadata" playsInline className="w-full aspect-video object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {op.floorplan_urls?.length > 0 && (
        <section className="pb-20 lg:pb-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto" data-testid="operation-floorplans">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-zinc-100 mb-10">{t("op_floorplans")}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {op.floorplan_urls.map((url, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="border border-amber-500/15 bg-[#121216] p-6" data-testid={`floorplan-item-${i}`}>
                  <img src={url} alt={`Planimetria ${i + 1}`} loading="lazy" className="w-full object-contain" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <footer className="border-t border-amber-500/10 bg-[#0D0D10]">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-600">© 2026 Capital Stone S.r.l. · P.IVA 14417030963 · REA MI-2781885</p>
          <a href="/privacy" data-testid="op-privacy-link" className="text-xs text-zinc-600 hover:text-[#D4AF37] transition-colors duration-300 order-first sm:order-none">
            Privacy Policy
          </a>
          <Link to="/" className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-zinc-400 hover:text-[#D4AF37] transition-colors duration-300">
            <ArrowLeft size={13} /> {t("op_back")}
          </Link>
        </div>
      </footer>
    </main>
  );
}
